import { z } from 'zod';

/**
 * Zod schema for validating required environment variables
 */
const envSchema = z.object({
  // Microsoft Azure App Registration Configuration
  VITE_AUTH_AUTHORITY: z.url('VITE_AUTH_AUTHORITY must be a valid URL'),
  VITE_AUTH_CLIENT_ID: z.string().min(1, 'VITE_AUTH_CLIENT_ID is required'),
  VITE_DATA_MISSION_CLIENT_ID: z.string().min(1, 'VITE_DATA_MISSION_CLIENT_ID is required'),

  // Application Insights Configuration
  VITE_APPINSIGHTS_CONNECTION_STRING: z.string().optional(),

  // API Configuration
  VITE_API_BASE_URL: z.url('VITE_API_BASE_URL must be a valid URL').optional(),
  VITE_AUTH_BASE_URL: z.url('VITE_AUTH_BASE_URL must be a valid URL'),

  // ETL Configuration
  VITE_ETL_REGIONS: z.preprocess((val) => {
    const str = val as string;
    // Replace single quotes with double quotes to support GitHub's variable format
    const normalized = str.replace(/'/g, '"');
    return JSON.parse(normalized) as unknown;
  }, z.array(z.string())),

  // ETL NAT Gateway static IPs per region (regionId -> IP address)
  VITE_ETL_NAT_IPS: z.preprocess((val) => {
    if (!val) return {};
    const str = val as string;
    const normalized = str.replace(/'/g, '"');
    return JSON.parse(normalized) as unknown;
  }, z.record(z.string(), z.string())).optional().default({}),
});

/**
 * Validates and parses environment variables
 * Throws an error with detailed information if validation fails
 */
function validateEnv() {
  const result = envSchema.safeParse(import.meta.env);

  if (!result.success) {
    const errors = z.treeifyError(result.error);
    console.error('❌ Invalid environment variables:', errors);

    // Create a user-friendly error message
    const errorMessages = Object.entries(errors)
      .filter(([key]) => key !== '_errors')
      .map(([key, value]) => {
        const messages = (value as { _errors: string[] })._errors;
        return `  - ${key}: ${messages.join(', ')}`;
      })
      .join('\n');

    // eslint-disable-next-line no-restricted-syntax
    throw new Error(
      `Environment validation failed:\n${errorMessages}\n\nPlease check your .env file and ensure all required variables are set.`
    );
  }

  return result.data;
}

/**
 * Validated and typed environment variables
 * Import this instead of using import.meta.env directly
 */
export const env = validateEnv();
